import api from './api';

export interface LanguageData {
    code: string;
    name: string;
    native_name?: string;
    is_active: boolean;
    is_default: boolean;
    is_complete?: boolean;
    flag_url?: string;
    flag?: string; // Enhanced on frontend
    is_rtl?: boolean;
    alt?: string;
    display_name?: string;
}

// Cache for available languages (5 minute TTL)
let availableLanguagesCache: LanguageData[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes


// Helper to map code to flag URL (fallback)
const getFlagFallback = (code: string) => {
    const map: Record<string, string> = {
        'en': 'https://flagcdn.com/w40/gb.png',
        'fr': 'https://flagcdn.com/w40/fr.png',
        'ar': 'https://flagcdn.com/w40/tn.png',
        'de': 'https://flagcdn.com/w40/de.png',
        'it': 'https://flagcdn.com/w40/it.png',
        'es': 'https://flagcdn.com/w40/es.png',
    };
    return map[code] || `https://flagcdn.com/w40/${code}.png`;
};

/**
 * Get all active languages (admin use)
 */
export const getLanguages = async (): Promise<LanguageData[]> => {
    try {
        const response = await api.get<LanguageData[]>('/languages');

        // Filter to only active languages
        const languages = response.data
            .filter(lang => lang.is_active)
            .map(lang => ({
                ...lang,
                // Use backend flag_url if available, otherwise fallback to helper
                flag: lang.flag_url || getFlagFallback(lang.code),
                display_name: lang.native_name || lang.name,
                alt: `${lang.name} Flag`
            }));

        return languages;
    } catch (error) {
        console.error('Failed to fetch languages', error);
        // Fallback if offline
        return [
            { code: 'en', name: 'English', is_active: true, is_default: true, flag: getFlagFallback('en'), alt: 'UK Flag' }
        ];
    }
};

/**
 * Get only languages with complete required translations (public use)
 */
export const getAvailableLanguages = async (): Promise<LanguageData[]> => {
    // Check if cache is still valid
    if (availableLanguagesCache && cacheTimestamp) {
        const now = Date.now();
        if (now - cacheTimestamp < CACHE_DURATION) {
            return availableLanguagesCache;
        }
    }

    try {
        const response = await api.get<{ languages: LanguageData[] }>('/languages/available');

        const languages = response.data.languages.map(lang => ({
            ...lang,
            flag: lang.flag_url || lang.flag || getFlagFallback(lang.code),
            display_name: lang.native_name || lang.name,
            alt: `${lang.name} Flag`
        }));

        // Always ensure English is available as fallback
        if (!languages.find(l => l.code === 'en')) {
            languages.unshift({
                code: 'en',
                name: 'English',
                native_name: 'English',
                is_active: true,
                is_default: true,
                is_complete: true,
                flag: getFlagFallback('en'),
                display_name: 'English',
                alt: 'UK Flag'
            });
        }

        // Update cache
        availableLanguagesCache = languages;
        cacheTimestamp = Date.now();

        return languages;
    } catch (error) {
        console.error('Failed to fetch available languages', error);
        // Fallback to English only
        return [
            {
                code: 'en',
                name: 'English',
                native_name: 'English',
                is_active: true,
                is_default: true,
                is_complete: true,
                flag: getFlagFallback('en'),
                display_name: 'English',
                alt: 'UK Flag'
            }
        ];
    }
};

/**
 * Clear the languages cache
 */
export const clearLanguagesCache = () => {
    availableLanguagesCache = null;
    cacheTimestamp = null;
};
